import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  getClubTeams,
  getClubAthletes,
  type ClubAthlete,
} from "../../../services/api";
import { useAuth } from "../../../context/AuthContext";
import MobileAppLayout from "../../../layouts/MobileAppLayout";
import { GameIcon, GameCard, GameHero, SegmentedControl, StatTile } from "../../../components/game";

interface TeamWithPoints {
  id: string;
  name: string;
  sport_type: string | null;
  totalPoints: number;
  athleteCount: number;
}

export default function AppCoachPoints() {
  const { t } = useTranslation();
  const { clubs: ctxClubs, activeClubId: ctxClubId } = useAuth();

  const [teams, setTeams] = useState<TeamWithPoints[]>([]);
  const [athletes, setAthletes] = useState<ClubAthlete[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [view, setView] = useState<"club" | "team" | "athletes">("club");
  const [selectedTeamId, setSelectedTeamId] = useState<string | null>(null);

  const activeClubId = ctxClubId || ctxClubs[0]?.id;
  const currentClub = ctxClubs.find(c => c.id === activeClubId);

  useEffect(() => {
    const loadData = async () => {
      if (!activeClubId) return;
      setLoading(true);
      setError(false);
      try {
        const [teamsData, athletesData] = await Promise.all([
          getClubTeams(activeClubId),
          getClubAthletes(activeClubId),
        ]);

        // Calculate points per team
        const teamsWithPoints: TeamWithPoints[] = teamsData.map((team: any) => {
          const teamAthletes = athletesData.filter((a: ClubAthlete) => a.team?.id === team.id);
          const totalPoints = teamAthletes.reduce((sum: number, a: ClubAthlete) => sum + (a.stats?.points || 0), 0);
          return {
            id: team.id,
            name: team.name,
            sport_type: team.sport_type,
            totalPoints,
            athleteCount: teamAthletes.length,
          };
        });

        setTeams(teamsWithPoints);
        setAthletes(athletesData);
      } catch (error) {
        console.error("Failed to load points data:", error);
        setError(true);
      }
      setLoading(false);
    };
    loadData();
  }, [activeClubId]);

  // Calculate totals
  const totalClubPoints = athletes.reduce((sum, a) => sum + (a.stats?.points || 0), 0);
  const totalAthletes = athletes.length;

  // Filter athletes by selected team
  const filteredAthletes = selectedTeamId
    ? athletes.filter(a => a.team?.id === selectedTeamId)
    : athletes;

  // Sort athletes by points (descending)
  const sortedAthletes = [...filteredAthletes].sort((a, b) => (b.stats?.points || 0) - (a.stats?.points || 0));

  // Get selected team name
  const selectedTeam = teams.find(t => t.id === selectedTeamId);

  // Rank badge tint (top 3 highlighted, rest neutral)
  const rankClass = (index: number) =>
    index === 0 ? "bg-amber-100 text-amber-700" :
    index === 1 ? "bg-slate-200 text-slate-600" :
    index === 2 ? "bg-orange-100 text-orange-700" :
    "bg-slate-100 text-slate-500";

  return (
    <MobileAppLayout>
      <div className="px-4 py-6 space-y-4">
        {/* Header */}
        <div className="flex items-center justify-between">
          <h1 className="text-xl font-bold text-slate-800">{t("coach.points", "Puncte")}</h1>
        </div>

        {/* View Tabs */}
        <SegmentedControl<"club" | "team" | "athletes">
          className="w-full [&>button]:flex-1"
          value={view}
          onChange={(v) => { setView(v); setSelectedTeamId(null); }}
          options={[
            { value: "club", label: t("coach.club", "Club") },
            { value: "team", label: t("coach.teams", "Echipe") },
            { value: "athletes", label: t("coach.athletes", "Sportivi") },
          ]}
        />

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="w-8 h-8 border-3 border-indigo-500 border-t-transparent rounded-full animate-spin"></div>
          </div>
        ) : error ? (
          <GameCard className="p-6 text-center">
            <h3 className="text-base font-bold text-slate-800 mb-1">{t("common.loadErrorTitle", "Ceva n-a mers")}</h3>
            <p className="text-sm text-slate-500">
              {t("common.loadError", "Nu am putut încărca datele. Verifică conexiunea și încearcă din nou.")}
            </p>
            <button
              onClick={() => window.location.reload()}
              className="mt-3 px-4 py-2 text-sm font-semibold rounded-lg bg-indigo-600 text-white"
            >
              {t("common.retry", "Reîncearcă")}
            </button>
          </GameCard>
        ) : (
          <>
            {/* Club View */}
            {view === "club" && (
              <div className="space-y-4">
                {/* Club Total Card */}
                <GameHero className="rounded-2xl p-6 shadow-[0_4px_0_#3730a3]">
                  <div className="text-center">
                    <p className="text-white/80 text-sm font-semibold">{currentClub?.name || t("coach.club", "Club")}</p>
                    <div className="flex items-center justify-center gap-2 mt-2">
                      <GameIcon name="star" className="w-8 h-8" />
                      <span className="text-4xl font-extrabold tabular-nums">{totalClubPoints.toLocaleString()}</span>
                    </div>
                    <p className="text-white/70 text-sm mt-2">{t("coach.totalPoints", "puncte totale")}</p>
                  </div>

                  <div className="grid grid-cols-2 gap-4 mt-6 pt-4 border-t border-white/20">
                    <div className="text-center">
                      <p className="text-2xl font-extrabold">{teams.length}</p>
                      <p className="text-white/70 text-xs">{t("coach.teams", "echipe")}</p>
                    </div>
                    <div className="text-center">
                      <p className="text-2xl font-extrabold">{totalAthletes}</p>
                      <p className="text-white/70 text-xs">{t("coach.athletes", "sportivi")}</p>
                    </div>
                  </div>
                </GameHero>

                {/* Teams Ranking */}
                <GameCard className="overflow-hidden">
                  <div className="px-4 py-3 bg-slate-50 border-b border-slate-100">
                    <h2 className="font-bold text-slate-800">{t("coach.teamRanking", "Clasament echipe")}</h2>
                  </div>
                  <div className="divide-y divide-slate-100">
                    {teams
                      .sort((a, b) => b.totalPoints - a.totalPoints)
                      .map((team, index) => (
                        <div key={team.id} className="flex items-center gap-4 p-4">
                          <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold text-sm ${rankClass(index)}`}>
                            {index + 1}
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="font-semibold text-slate-800 truncate">{team.name}</p>
                            <p className="text-xs text-slate-500">{team.athleteCount} sportivi</p>
                          </div>
                          <div className="flex items-center gap-1.5 text-right">
                            <GameIcon name="star" className="w-4 h-4" />
                            <div>
                              <p className="font-extrabold text-amber-600 tabular-nums">{team.totalPoints.toLocaleString()}</p>
                              <p className="text-xs text-slate-400">{t("coach.pts", "pts")}</p>
                            </div>
                          </div>
                        </div>
                      ))}
                  </div>
                </GameCard>
              </div>
            )}

            {/* Team View */}
            {view === "team" && !selectedTeamId && (
              <div className="space-y-3">
                {teams
                  .sort((a, b) => b.totalPoints - a.totalPoints)
                  .map(team => (
                    <button
                      key={team.id}
                      onClick={() => setSelectedTeamId(team.id)}
                      className="w-full text-left bg-white rounded-2xl border border-slate-100 p-4 shadow-[0_3px_0_#eef2f8] active:translate-y-[2px] transition-transform"
                    >
                      <div className="flex items-center gap-4">
                        <div className="w-14 h-14 rounded-2xl bg-amber-100 flex items-center justify-center shadow-[0_3px_0_#fde68a]">
                          <GameIcon name="star" className="w-7 h-7" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <h3 className="font-bold text-slate-800">{team.name}</h3>
                          <p className="text-sm text-slate-500">{team.athleteCount} sportivi</p>
                        </div>
                        <div className="text-right">
                          <p className="text-2xl font-extrabold text-amber-600 tabular-nums">{team.totalPoints.toLocaleString()}</p>
                          <p className="text-xs text-slate-400">{t("coach.pts", "puncte")}</p>
                        </div>
                      </div>
                    </button>
                  ))}
              </div>
            )}

            {/* Team Detail View */}
            {view === "team" && selectedTeamId && (
              <div className="space-y-4">
                <button
                  onClick={() => setSelectedTeamId(null)}
                  className="flex items-center gap-2 text-slate-600 hover:text-slate-800"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                  </svg>
                  <span className="text-sm font-medium">{t("coach.back", "Înapoi")}</span>
                </button>

                {/* Team Header */}
                <GameHero className="rounded-2xl p-5 shadow-[0_4px_0_#3730a3]">
                  <h2 className="font-extrabold text-xl">{selectedTeam?.name}</h2>
                  <div className="flex items-center gap-2 mt-3">
                    <GameIcon name="star" className="w-6 h-6" />
                    <span className="text-3xl font-extrabold tabular-nums">{selectedTeam?.totalPoints.toLocaleString()}</span>
                    <span className="text-white/70">{t("coach.pts", "puncte")}</span>
                  </div>
                </GameHero>

                {/* Team Athletes */}
                <GameCard className="overflow-hidden">
                  <div className="px-4 py-3 bg-slate-50 border-b border-slate-100">
                    <h3 className="font-bold text-slate-800">{t("coach.athleteRanking", "Clasament sportivi")}</h3>
                  </div>
                  <div className="divide-y divide-slate-100">
                    {sortedAthletes.map((athlete, index) => (
                      <div key={athlete.id} className="flex items-center gap-4 p-4">
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold text-sm ${rankClass(index)}`}>
                          {index + 1}
                        </div>
                        <div className="w-10 h-10 rounded-full bg-gradient-to-br from-indigo-500 to-blue-600 flex items-center justify-center text-white font-semibold">
                          {athlete.user?.full_name?.charAt(0) || "?"}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-slate-800 truncate">{athlete.user?.full_name || t("coach.unnamed", "Fără nume")}</p>
                        </div>
                        <div className="flex items-center gap-1.5 text-right">
                          <GameIcon name="star" className="w-4 h-4" />
                          <p className="font-extrabold text-amber-600 tabular-nums">{(athlete.stats?.points || 0).toLocaleString()}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </GameCard>
              </div>
            )}

            {/* Athletes View */}
            {view === "athletes" && (
              <div className="space-y-4">
                {/* Filter by Team */}
                {teams.length > 1 && (
                  <select
                    value={selectedTeamId || ""}
                    onChange={e => setSelectedTeamId(e.target.value || null)}
                    className="w-full px-4 py-3 bg-white border border-slate-200 rounded-2xl text-slate-800 shadow-[0_3px_0_#eef2f8] focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  >
                    <option value="">{t("coach.allTeams", "Toate echipele")}</option>
                    {teams.map(team => (
                      <option key={team.id} value={team.id}>{team.name}</option>
                    ))}
                  </select>
                )}

                {/* Athletes summary */}
                <div className="grid grid-cols-2 gap-3">
                  <StatTile
                    icon="star"
                    color="#f59e0b"
                    label={t("coach.totalPoints", "puncte totale")}
                    value={sortedAthletes.reduce((sum, a) => sum + (a.stats?.points || 0), 0).toLocaleString()}
                  />
                  <StatTile
                    icon="trophy"
                    color="#4f46e5"
                    label={t("coach.athletes", "sportivi")}
                    value={sortedAthletes.length}
                  />
                </div>

                {/* Athletes Ranking */}
                <GameCard className="overflow-hidden">
                  <div className="px-4 py-3 bg-amber-50 border-b border-amber-100 flex items-center justify-between">
                    <h3 className="font-bold text-amber-800">{t("coach.topAthletes", "Top sportivi")}</h3>
                    <span className="text-sm text-amber-600">{sortedAthletes.length} total</span>
                  </div>
                  <div className="divide-y divide-slate-100">
                    {sortedAthletes.map((athlete, index) => (
                      <div key={athlete.id} className="flex items-center gap-4 p-4">
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold text-sm ${rankClass(index)}`}>
                          {index + 1}
                        </div>
                        <div className="w-10 h-10 rounded-full bg-gradient-to-br from-indigo-500 to-blue-600 flex items-center justify-center text-white font-semibold">
                          {athlete.user?.full_name?.charAt(0) || "?"}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-slate-800 truncate">{athlete.user?.full_name || t("coach.unnamed", "Fără nume")}</p>
                          {athlete.team && (
                            <p className="text-xs text-slate-500">{athlete.team.name}</p>
                          )}
                        </div>
                        <div className="flex items-center gap-1.5 text-right">
                          <GameIcon name="star" className="w-4 h-4" />
                          <div>
                            <p className="font-extrabold text-amber-600 tabular-nums">{(athlete.stats?.points || 0).toLocaleString()}</p>
                            <p className="text-xs text-slate-400">{t("coach.pts", "pts")}</p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </GameCard>
              </div>
            )}
          </>
        )}
      </div>
    </MobileAppLayout>
  );
}
